import { useEffect } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { useHotelGuestCount } from "@apac/hooks";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { SimplePageHeader } from "@hsbc/portal";
import { HOTELS_SEARCH_CALENDAR_PATH } from "@hsbc/utilities";
import {
  HotelLocationAutocomplete,
  HotelsRawEventName,
  useDateRangePicker,
  useEmitCoreHotelsRawEvent,
  useHotelSearch,
} from "@tenant/hotels";
import {
  Button,
  ButtonTypeEnum,
  Calendar,
  Icon,
  Page,
  PickerType,
} from "@tenant/ui";
import styles from "./styles.module.scss";

export const enum HOTEL_SEARCH_MODE {
  LOCATION = "location",
  CALENDAR = "calendar",
}

interface HotelSearchDialogProps {
  mode: HOTEL_SEARCH_MODE;
}

export const HotelSearchDialog = (props: HotelSearchDialogProps) => {
  const { mode } = props;
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "HotelSearchDialog");

  const { context, handlers } = useHotelSearch();
  const navigate = useNavigate();

  const { formattedGuestCountString } = useHotelGuestCount();

  const goBack = () => navigate(-1);
  const toCalendar = () =>
    navigate(HOTELS_SEARCH_CALENDAR_PATH, { replace: true });

  const calendarHeader = () => {
    return (
      <div className={cn("calendar-header")}>
        <button
          className={cn("back-button")}
          onClick={goBack}
          aria-label={t("back")}
        >
          <Icon className={cn("back-icon")} iconName={IconNameEnum.backIcon} />
        </button>
        <div className={cn("title-container")}>
          <h1 className={cn("title")}>
            {t("core-hotels.search.dateSelection.title")}
          </h1>
          <div className={cn("location")}>{context.place?.label}</div>
          <div className={cn("passenger")}>
            {`${formattedGuestCountString()} | ${t(
              "recentSearch.hotels.rooms",
              {
                count: context.roomsCount ?? 1,
              }
            )}`}
          </div>
        </div>
        <div className={cn("right-button")} />
      </div>
    );
  };

  return (
    <Page
      header={
        mode === HOTEL_SEARCH_MODE.CALENDAR ? (
          calendarHeader()
        ) : (
          <SimplePageHeader
            title={t("searchControl.destinationHeader")}
            onBack={goBack}
          />
        )
      }
      footer={<></>}
      className={block}
    >
      {mode === HOTEL_SEARCH_MODE.LOCATION && (
        <HotelLocationAutocomplete
          id="hotel-location-autocomplete"
          label={t("home.locationSearchLabel")}
          value={context.place}
          onChange={handlers.handlePlaceChange}
          onClose={toCalendar}
          noDialog
        />
      )}
      {mode === HOTEL_SEARCH_MODE.CALENDAR && (
        <HotelCalendar
          dateRange={context.dateRange}
          onStartDateChange={handlers.handleDateRangeFromChange}
          onEndDateChange={handlers.handleDateRangeUntilChange}
          onClose={goBack}
        />
      )}
    </Page>
  );
};

const HotelCalendar = (props: {
  dateRange: { from?: string; until?: string };
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onClose: () => void;
}) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "HotelCalendar");
  const { context, handlers, text } = useDateRangePicker({
    dialogTitle: t("chooseDates"),
    startDate: props.dateRange?.from,
    endDate: props.dateRange?.until,
    onStartDateChangeHandler: props.onStartDateChange,
    onEndDateChangeHandler: props.onEndDateChange,
    onDoneClick: props.onClose,
  });

  const emitCalendarOpened = useEmitCoreHotelsRawEvent(
    HotelsRawEventName.HotelCalendarOpened
  );

  useEffect(() => {
    emitCalendarOpened({});
  }, []);

  return (
    <div className={block}>
      <Calendar
        className={cn("calendar")}
        viewType={context.calendarViewType}
        setStartDate={handlers.onStartDateChange}
        setEndDate={handlers.onEndDateChange}
        startDate={context.startDate}
        endDate={context.endDate}
        startDateLabel={text.startDateLabel}
        endDateLabel={text.endDateLabel}
        pickerType={PickerType.RANGE}
        priceTagsElement={<></>}
        months={[]}
        currency={""}
        priceTags={[]}
      />
      <Button
        buttonType={ButtonTypeEnum.Primary}
        className={cn("done-button")}
        onClick={handlers.onDoneClick}
      >
        {t("continue")}
      </Button>
    </div>
  );
};
